import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { TwoFactorVerify } from './Auth/TwoFactorVerify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const canvasRef = useRef(null);

  const subtitleText = "Initializing Secure Access...";

  // Typing effect
  useEffect(() => {
    let index = 0;
    let isDeleting = false;
    
    const typeInterval = setInterval(() => {
      if (!isDeleting) {
        if (index <= subtitleText.length) {
          setTypedText(subtitleText.slice(0, index));
          index++;
        } else {
          setTimeout(() => { isDeleting = true; }, 2000);
        }
      } else {
        if (index > 0) {
          index--;
          setTypedText(subtitleText.slice(0, index));
        } else {
          isDeleting = false;
        }
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, []);

  // Cursor blink
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorInterval);
  }, []);

  // Matrix rain effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const chars = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);

    const draw = () => {
      ctx.fillStyle = 'rgba(3, 6, 10, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = '#00F5FF';
      ctx.font = `${fontSize}px monospace`;
      
      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillStyle = Math.random() > 0.98 ? '#00FF41' : 'rgba(0, 245, 255, 0.6)';
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 50);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use axios like regular login to avoid fetch interception issues
      const response = await axios.post(`${API_URL}/api/auth/admin-login`, {
        email,
        password
      });

      const data = response.data;

      // Store token and redirect
      localStorage.setItem('token', data.access_token);
      toast.success('ACCESS GRANTED - Welcome, Administrator', {
        style: { background: '#0a1a0a', color: '#00FF41', border: '1px solid #00FF41' }
      });
      
      // Use window.location for a clean navigation that bypasses React state race conditions
      window.location.href = '/admin';

    } catch (error) {
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail || 'Authentication failed';
      
      // Handle 2FA requirement
      if (statusCode === 202 || errorDetail.includes('Two-factor authentication')) {
        setShow2FA(true);
        toast.info('2FA code sent to your email', {
          style: { background: '#0a1a0a', color: '#00F5FF', border: '1px solid #00F5FF' }
        });
        setLoading(false);
        return;
      }
      
      toast.error(errorDetail, {
        style: { background: '#1a0a0a', color: '#ff4444', border: '1px solid #ff4444' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handle2FASuccess = async () => {
    toast.success('ACCESS GRANTED - Welcome, Administrator', {
      style: { background: '#0a1a0a', color: '#00FF41', border: '1px solid #00FF41' }
    });
    window.location.href = '/admin';
  };

  // Show 2FA verification screen with cyberpunk styling
  if (show2FA) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center p-4" style={{ background: '#03060A' }}>
        {/* Matrix Canvas Background */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 z-0"
          style={{ opacity: 0.7 }}
        />
        <div className="relative z-10 w-full max-w-md">
          <TwoFactorVerify 
            email={email}
            onBack={() => setShow2FA(false)}
            onSuccess={handle2FASuccess}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#03060A' }}>
      {/* Matrix Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
        style={{ opacity: 0.7 }}
      />

      {/* Grid Overlay */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite'
        }}
      />

      {/* Radial Overlay */}
      <div 
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(3, 6, 10, 0.8) 100%)'
        }}
      />

      {/* Main Content */}
      <div className="relative z-20 min-h-screen flex items-center justify-center p-4">
        <div 
          className="w-full max-w-md relative"
          style={{
            background: 'rgba(3, 6, 10, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 245, 255, 0.2)',
            borderRadius: '16px',
            boxShadow: '0 0 40px rgba(0, 245, 255, 0.1), inset 0 0 60px rgba(0, 245, 255, 0.02)',
            overflow: 'hidden'
          }}
        >
          {/* Scan Line Animation */}
          <div 
            className="absolute inset-0 pointer-events-none z-30"
            style={{
              background: 'linear-gradient(180deg, transparent 0%, rgba(0, 245, 255, 0.03) 50%, transparent 100%)',
              height: '100px',
              animation: 'scanLine 3s linear infinite'
            }}
          />

          {/* Corner Accents */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-cyan-400/50" />
          <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-cyan-400/50" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-cyan-400/50" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-cyan-400/50" />

          {/* Card Content */}
          <div className="p-8">
            {/* Header */}
            <div className="text-center mb-8">
              {/* Logo with Matrix Effect */}
              <div className="flex justify-center mb-4">
                <div 
                  className="relative p-3 rounded-xl"
                  style={{
                    background: 'rgba(0, 245, 255, 0.05)',
                    border: '1px solid rgba(0, 245, 255, 0.2)',
                    boxShadow: '0 0 20px rgba(0, 245, 255, 0.1)'
                  }}
                >
                  <img 
                    src="/formlio_final_logo.png" 
                    alt="Formlio" 
                    className="h-12 w-auto"
                    style={{
                      filter: 'brightness(0) saturate(100%) invert(89%) sepia(47%) saturate(1032%) hue-rotate(101deg) brightness(103%) contrast(103%)',
                    }}
                  />
                </div>
              </div>
              
              {/* Title */}
              <h1 
                className="text-3xl font-bold mb-2 tracking-wider"
                style={{
                  fontFamily: "'Orbitron', sans-serif",
                  background: 'linear-gradient(135deg, #00F5FF 0%, #00FF41 50%, #8B5CF6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 30px rgba(0, 245, 255, 0.5)'
                }}
              >
                Formlio
              </h1>
              <p 
                className="text-sm tracking-widest"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.7)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
              >
                Build forms that convert & delight
              </p>

              {/* Status Indicator */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{
                    background: '#00FF41',
                    boxShadow: '0 0 10px #00FF41, 0 0 20px #00FF41',
                    animation: 'pulse 2s infinite'
                  }}
                />
                <span 
                  className="text-xs tracking-wider"
                  style={{ color: '#00FF41', fontFamily: "'Rajdhani', sans-serif" }}
                >
                  SYSTEM ONLINE
                </span>
              </div>

              {/* Typing Text */}
              <p 
                className="mt-4 text-sm h-6"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.6)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
              >
                {typedText}
                <span 
                  className="inline-block w-2 h-4 ml-1 align-middle"
                  style={{ 
                    background: showCursor ? '#00F5FF' : 'transparent',
                    transition: 'background 0.1s'
                  }}
                />
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400/60">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Admin Email"
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-300"
                  style={{
                    background: 'rgba(3, 6, 10, 0.6)',
                    border: '1px solid rgba(0, 245, 255, 0.2)',
                    color: '#E2E8F0',
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: '15px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.6)';
                    e.target.style.boxShadow = '0 0 20px rgba(0, 245, 255, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  data-testid="admin-login-email"
                />
              </div>

              {/* Password Input */}
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-400/60">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-300"
                  style={{
                    background: 'rgba(3, 6, 10, 0.6)',
                    border: '1px solid rgba(0, 245, 255, 0.2)',
                    color: '#E2E8F0',
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: '15px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.6)';
                    e.target.style.boxShadow = '0 0 20px rgba(0, 245, 255, 0.15)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  data-testid="admin-login-password"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-lg font-semibold tracking-wider uppercase transition-all duration-300 relative overflow-hidden group"
                style={{
                  background: loading 
                    ? 'rgba(0, 245, 255, 0.1)' 
                    : 'linear-gradient(135deg, rgba(0, 245, 255, 0.2) 0%, rgba(0, 255, 65, 0.2) 100%)',
                  border: '1px solid rgba(0, 245, 255, 0.4)',
                  color: '#00F5FF',
                  fontFamily: "'Orbitron', sans-serif",
                  fontSize: '14px'
                }}
                data-testid="admin-login-submit"
              >
                {/* Glow Effect */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.3) 0%, rgba(0, 255, 65, 0.3) 100%)',
                  }}
                />
                
                <span className="relative z-10">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Authenticating...
                    </span>
                  ) : (
                    'Access System'
                  )}
                </span>
              </button>
            </form>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p 
                className="text-xs"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.4)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
              >
                Authorized Personnel Only
              </p>
              <p 
                className="text-xs mt-1"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.3)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
              >
                All access attempts are monitored and logged
              </p>
              <Link 
                to="/login"
                className="inline-block mt-4 text-xs transition-colors duration-200"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.5)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
                onMouseEnter={(e) => e.target.style.color = '#00F5FF'}
                onMouseLeave={(e) => e.target.style.color = 'rgba(0, 245, 255, 0.5)'}
                data-testid="regular-login-link"
              >
                &larr; Return to Standard Login
              </Link>
              <p 
                className="text-xs mt-3"
                style={{ 
                  color: 'rgba(0, 245, 255, 0.3)',
                  fontFamily: "'Rajdhani', sans-serif"
                }}
              >
                Formlio — A Product by MetaNex
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        @keyframes scanLine {
          0% { transform: translateY(-100px); }
          100% { transform: translateY(500px); }
        }
        
        @keyframes gridMove {
          0% { background-position: 0 0; }
          100% { background-position: 50px 50px; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        input::placeholder {
          color: rgba(0, 245, 255, 0.4);
        }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #E2E8F0;
          -webkit-box-shadow: 0 0 0px 1000px rgba(3, 6, 10, 0.9) inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>
    </div>
  );
};

export default AdminLoginPage;
